/**
 * The clear effect, derived from the board look: the particle burst on a
 * line clear, matching `docs/DESIGN.md`'s "The board" (owner amendment,
 * 2026-10-04): Keyline is flat fills and keylines with no glow anywhere, and
 * a clear cuts rather than fades, so the burst is hard shards in the cleared
 * cell's own colour, moving in straight lines and vanishing abruptly.
 *
 * `packages/themes` owns the tokens and the cell painter and knows nothing
 * of particles (ADR 0006/0022's split); this reads `ThemeTokens` only, the
 * same pattern `canvas2d-renderer.ts`'s `deriveWellInk` uses, so every
 * colour traces back to a token. One look, so one profile: the six per-theme
 * profiles went with the six retired themes (`docs/decisions/0056`). Both variants
 * share it; only their tokens differ.
 */
import type { PaintKind, Theme, ThemeTokens } from '@interlock/themes';

export type ParticleShape = 'spark' | 'shard' | 'mote' | 'ember';
/** `'cut'`: alpha holds at 1 until near the very end of life, then snaps to 0, the board look's "cuts instead of fades". `'linear'`: an ordinary fade-out (the bursts of the hold, the specials). */
export type ParticleFade = 'linear' | 'cut';

export interface ClearEffectProfile {
  readonly shape: ParticleShape;
  readonly glow: boolean;
  /** Canvas-pixel blur cap when `glow` is true, `0` otherwise. */
  readonly maxBlurPx: number;
  readonly fade: ParticleFade;
  /** Cell-units/second² downward pull. Negative rises. */
  readonly gravity: number;
  /** Outward launch speed scale, in cell-units/second. */
  readonly speed: number;
  readonly lifeMs: number;
  /** The board's own accent: the quad shockwave ring and the back-to-back accent ring. */
  readonly accent: string;
  /** Resolves a cleared cell's paint kind to the colour a particle spawned from it should use. */
  readonly colorFor: (kind: PaintKind) => string;
}

function cellColor(tokens: ThemeTokens, kind: PaintKind): string {
  return kind === 'G' ? tokens.garbage : tokens.pieces[kind];
}

/**
 * The board look's clear-effect physics and colouring, from its own tokens.
 * A plain function of `ThemeTokens` (not a method on `Theme`) so it is
 * testable directly, without a canvas (`test/render/theme-effects.test.ts`).
 */
export function deriveClearEffectProfile(tokens: ThemeTokens): ClearEffectProfile {
  return {
    shape: 'shard',
    glow: false,
    maxBlurPx: 0,
    fade: 'cut',
    gravity: 0,
    speed: 3.6,
    lifeMs: 260,
    accent: tokens.accent,
    colorFor: (kind) => cellColor(tokens, kind),
  };
}

/** Convenience over `deriveClearEffectProfile(theme.tokens)` — the shape every call site actually has on hand. */
export function clearEffectProfileFor(theme: Theme): ClearEffectProfile {
  return deriveClearEffectProfile(theme.tokens);
}
